/**
 * ConfigTab — Accountability settings for one customer.
 *
 * Every string on this surface is written for the person operating the platform, not for the
 * engineer who built it. Each message says what is true, what it means for them, and what to do
 * next. No internal vocabulary (index plans, ticks, floors, blast radius) reaches the screen.
 *
 * AUTHORIZATION
 * The server is the boundary — AccountabilityConfigController routes every call through
 * SecurityContext.canBypassScopeForCustomer (Platform Admin: any customer; Super Admin: their own
 * only). The role check here decides what to RENDER; it is not a security control.
 *
 * The effective mode is computed server-side and displayed as given. It is never recomputed here,
 * so the screen cannot disagree with what the platform is actually doing.
 */
import React, { useCallback, useEffect, useState } from 'react';
import * as api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Pill, Label, Input, EmptyState, Skeleton, EntityPicker } from '../../components/v2';
import { ShieldAlert, ShieldCheck, Eye, Power, Siren, AlertTriangle } from 'lucide-react';

const MODES = [
  {
    id: 'OFF',
    label: 'Off',
    icon: <Power size={14} />,
    blurb: 'Nothing is checked for this customer. If someone leaves the company, the systems they '
         + 'were responsible for will keep showing their name, and no one will be told.',
  },
  {
    id: 'SHADOW',
    label: 'Review only',
    icon: <Eye size={14} />,
    blurb: 'Find and list systems whose responsible owner has left, without changing anything. '
         + 'Start here: it shows you how many systems are affected before you commit to acting on them.',
  },
  {
    id: 'ENFORCE',
    label: 'Act automatically',
    icon: <ShieldCheck size={14} />,
    blurb: 'Act on what is found. A system that had an owner is marked as needing a new one; a '
         + 'pending nomination for someone who has left is cancelled so the system can be claimed '
         + 'again. Every change is recorded in the audit trail with who and when.',
  },
];

export default function ConfigTab({ active, customerId: customerIdProp = null }) {
  const auth = useAuth() || {};
  // §8.4 — session role is authoritative; app_users.role is only a fallback.
  const role = auth.currentRole;
  const isPlatformAdmin = role === 'Platform Admin';
  const canManage = isPlatformAdmin || role === 'Super Admin';

  // The customer comes from the page, which owns the one picker for this whole surface. A second
  // picker here could sit on a different customer than the tabs beside it — on a page whose job is
  // stating who owns what, two answers on screen at once is the worst possible outcome.
  const customerId = customerIdProp;
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  // Turning enforcement on is the one control here that reaches out to real people, and on an
  // estate with a standing backlog it opens work for every system that already has no owner.
  // It gets a second step; nothing else on this page does.
  const [confirmEnforce, setConfirmEnforce] = useState(false);

  const load = useCallback(async () => {
    if (isPlatformAdmin && !customerId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getAccountabilityConfig(isPlatformAdmin ? customerId : undefined);
      setCfg(res && res.data !== undefined ? res.data : res);
    } catch (e) {
      setError('We could not load these settings. Refresh the page to try again — nothing has been changed.');
      setCfg(null);
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin, customerId]);

  useEffect(() => { if (active !== false) load(); }, [load, active]);

  // The picker's options. Fetched separately from the config because the config is read on every
  // visit to this surface while this list is only needed for one card, and it grows with the
  // customer's estate whereas the config is a single row.
  const loadPolicies = useCallback(async (target) => {
    if (!target) { setPolicies([]); return; }
    setPoliciesLoading(true);
    try {
      const res = await api.listAccountabilityEscalationPolicies(target);
      const payload = res && res.data !== undefined ? res.data : res;
      setPolicies(Array.isArray(payload?.policies) ? payload.policies : []);
    } catch (e) {
      // Not fatal and not surfaced as an error banner: the settings above are still readable and
      // changeable. The card below says what is missing and what to do about it.
      setPolicies([]);
    } finally {
      setPoliciesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active === false) return;
    loadPolicies(cfg?.customerId || customerId);
  }, [active, loadPolicies, cfg?.customerId, customerId]);

  // A customer switch must not carry a half-finished confirmation across to the next customer.
  useEffect(() => { setConfirmEnforce(false); }, [customerId]);

  const patch = async (body, what) => {
    const target = cfg?.customerId || customerId;
    if (!target) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.setAccountabilityConfig(target, body);
      const saved = res && res.data !== undefined ? res.data : res;
      setCfg(saved);
      // The confirmation has to describe what the operator actually changed. This branch used to
      // append "This customer is now set to <mode>" to EVERY save, so changing the batch size
      // confirmed a mode nobody had touched, and would now have done the same for the escalation
      // policy. The mode sentence belongs only to a mode change.
      const modeChanged = Object.prototype.hasOwnProperty.call(body, 'leaverMode');
      if (modeChanged && saved.effectiveRaisedByPlatform) {
        setNotice(`${what} saved. A platform-wide setting keeps checks running as “${modeLabel(saved.effectiveLeaverMode)}” for every customer, so that is what applies here. You can choose a stronger option, but not a weaker one.`);
      } else if (modeChanged) {
        setNotice(`${what} saved. This customer is now set to “${modeLabel(saved.effectiveLeaverMode)}”, and the change takes effect within a few minutes.`);
      } else if (Object.prototype.hasOwnProperty.call(body, 'reassignmentPolicyId')) {
        setNotice(body.reassignmentPolicyId
          ? `${what} saved. New tasks for systems that lose their owner will be routed using this policy.`
          : `${what} cleared. New tasks will follow this organization’s usual escalation route.`);
      } else {
        setNotice(`${what} saved. The change takes effect within a few minutes.`);
      }
    } catch (e) {
      setError(`We could not save ${what.toLowerCase()}. Your previous setting is still in place. ${friendly(e)}`);
    } finally {
      setSaving(false);
    }
  };

  // The policy actually in force, and the sentence that follows from it. Resolved from the two
  // server-sent facts (the stored id, and whether it is still usable) rather than re-derived here,
  // so the screen cannot claim something the runtime would not do.
  const boundPolicy = policies.find((p) => p.id === cfg?.reassignmentPolicyId) || null;
  const pending = Number(cfg?.pendingEscalationCount || 0);

  const chooseMode = (m) => {
    if (m.id === 'ENFORCE' && (cfg?.leaverMode || '').toUpperCase() !== 'ENFORCE') {
      setConfirmEnforce(true);
      setNotice(null);
      setError(null);
      return;
    }
    setConfirmEnforce(false);
    patch({ leaverMode: m.id }, `“${m.label}”`);
  };

  if (!canManage) {
    return (
      <Card padding="none">
        <EmptyState
          icon={<ShieldAlert size={22} />}
          title="You don't have access to these settings"
          description="Accountability settings are managed by a Platform Admin, or by a Super Admin for their own customer. If you need a change here, ask one of them — you can keep using every other tab on this page."
        />
      </Card>
    );
  }

  if (loading && !cfg) return <Skeleton lines={6} />;

  return (
    <div className="space-y-4">
      {error && <Card><div className="text-v2-danger text-[13px]">{error}</div></Card>}
      {notice && <Card><div className="text-[13px]">{notice}</div></Card>}

      {cfg && (
        <>
          <Card>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="font-semibold text-[14px]">When someone leaves the company</span>
              <Pill tone={cfg.effectiveLeaverMode === 'ENFORCE' ? 'success'
                        : cfg.effectiveLeaverMode === 'SHADOW' ? 'warning' : 'muted'} size="sm">
                Currently: {modeLabel(cfg.effectiveLeaverMode)}
              </Pill>
              {cfg.usingDefaults && <Pill tone="muted" size="sm">Not yet customised</Pill>}
            </div>
            <div className="text-[12.5px] text-v2-ink-muted mb-3">
              When a person is removed or deactivated, the systems they were accountable for still
              name them. Choose how much you want the platform to do about that.
            </div>
            {/* A platform-wide setting can RAISE what is in force above this customer's choice.
                Showing only the stored value would tell the operator the opposite of the truth.
                The server sends the FACT; the sentence is written here, so copy lives in one place. */}
            {cfg.effectiveRaisedByPlatform && (
              <div className="text-[12.5px] text-v2-ink-muted mb-3">
                A platform-wide setting keeps checks running as “{modeLabel(cfg.effectiveLeaverMode)}”
                for every customer. Your choice below can make this stricter, but cannot turn it off.
              </div>
            )}

            <div className="grid gap-2">
              {MODES.map((m) => {
                const selected = (cfg.leaverMode || '').toUpperCase() === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    disabled={saving}
                    onClick={() => chooseMode(m)}
                    className={`text-left rounded-v2-md border p-3 transition ${
                      selected ? 'border-v2-action shadow-v2-xs' : 'border-v2-border hover:border-v2-border-strong'
                    } ${saving ? 'opacity-60 cursor-wait' : ''}`}
                  >
                    <div className="flex items-center gap-2 text-[13px] font-semibold">
                      {m.icon}{m.label}
                      {selected && <Pill tone="brand" size="sm">Current choice</Pill>}
                    </div>
                    <div className="text-[12.5px] text-v2-ink-muted mt-1">{m.blurb}</div>
                  </button>
                );
              })}
            </div>

            {/* Turning enforcement on does not only cover departures from here on — the platform
                also works through systems that already have no owner. An operator who is not told
                that discovers it as a queue full of new work. The number comes from the server so
                it is the same population that will actually be acted on. */}
            {confirmEnforce && (
              <div className="mt-3 rounded-v2-md border border-v2-warning bg-v2-warning-soft p-3">
                <div className="flex items-center gap-2 text-[13px] font-semibold mb-1">
                  <AlertTriangle size={14} />
                  Before you turn this on
                </div>
                <div className="text-[12.5px] text-v2-ink-muted">
                  {pending > 0
                    ? `${pending} system${pending === 1 ? '' : 's'} on this customer already ${pending === 1 ? 'has' : 'have'} no accountable owner. Turning this on opens a task for each of them, and for every future departure. `
                    : 'No systems on this customer are currently without an owner, so nothing will be raised right now. From here on, a departure will open a task. '}
                  {boundPolicy
                    ? `Tasks will be routed using the “${boundPolicy.name}” escalation policy.`
                    : "No escalation policy has been chosen below, so tasks will follow this organization’s usual escalation route. You can set one first if you would rather."}
                </div>
                <div className="text-[12.5px] text-v2-ink-muted mt-2">
                  You can switch back to “Review only” at any time. Tasks already opened stay open
                  until the system has an owner again, and then close on their own.
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => { setConfirmEnforce(false); patch({ leaverMode: 'ENFORCE' }, '“Act automatically”'); }}
                    className="px-3 py-1.5 rounded-v2-sm border border-v2-action text-[13px] font-semibold hover:shadow-v2-xs disabled:opacity-60"
                  >
                    Turn on, and open {pending > 0 ? `${pending} task${pending === 1 ? '' : 's'}` : 'tasks from now on'}
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => setConfirmEnforce(false)}
                    className="px-3 py-1.5 rounded-v2-sm border border-v2-border text-[13px] hover:border-v2-border-strong disabled:opacity-60"
                  >
                    Keep current setting
                  </button>
                </div>
              </div>
            )}
          </Card>

          <Card>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Siren size={14} />
              <span className="font-semibold text-[14px]">Who to contact when a system loses its owner</span>
            </div>
            <div className="text-[12.5px] text-v2-ink-muted mb-3">
              When the platform finds a system with no accountable owner, it opens a task and routes
              it using an escalation policy — the ordered list of people to ask, with a response time
              for each step. Policies are created on the Escalation Policies page; choose which one
              applies here.
            </div>

            <Label>Escalation policy</Label>
            <EntityPicker
              items={policies}
              value={cfg.reassignmentPolicyId || null}
              loading={policiesLoading}
              disabled={saving}
              ariaLabel="Choose the escalation policy used when a system loses its owner"
              icon={<Siren size={14} className="text-v2-ink-muted shrink-0" />}
              onChange={(id) => patch({ reassignmentPolicyId: id }, 'The escalation policy')}
              getId={(p) => p.id}
              getLabel={(p) => p.name}
              getSublabel={(p) => [
                p.tierCount ? `${p.tierCount} step${p.tierCount === 1 ? '' : 's'}` : 'No steps defined',
                p.description,
              ].filter(Boolean).join(' · ')}
              getBadge={(p) => (p.builtForReassignment ? 'Built for this' : null)}
              /* Policies marked for this purpose sort first, then alphabetically. A hint about
                 intent, never a restriction — an org already running a general escalation chain
                 should not have to duplicate it to use it here. */
              getSortKey={(p) => `${p.builtForReassignment ? '0' : '1'}${String(p.name || '').toLowerCase()}`}
              placeholder="Choose an escalation policy"
              loadingText="Loading escalation policies…"
              searchPlaceholder="Search policies by name"
              allowClear
              clearLabel="Use this organization's usual escalation route"
              clearSublabel="No specific policy. Tasks follow the same route as other work for this organization."
              emptyMessage={(q, total) => (q
                ? `No policy matches “${q}”. Try part of the name, or clear the search to see all ${total}.`
                : 'No escalation policies exist for this customer yet. Create one on the Escalation Policies page, then come back here to choose it.')}
            />

            {/* The stored id and whether it is still usable are two different facts. A policy can be
                deactivated or deleted after it was chosen — there is deliberately no database
                constraint stopping that — and showing only the name would leave this screen
                claiming a route the platform silently bypasses. */}
            {cfg.reassignmentPolicyId && !cfg.reassignmentPolicyUsable && (
              <div className="mt-2 text-[12.5px] text-v2-danger">
                The policy chosen here is no longer available — it may have been deleted or turned
                off. Until you choose another one, tasks will follow this organization's usual
                escalation route. Nothing is lost; the tasks are still raised.
              </div>
            )}
            {!cfg.reassignmentPolicyId && (
              <div className="mt-2 text-[12.5px] text-v2-ink-muted">
                Nothing chosen. Tasks will go to whoever this organization's usual escalation route
                names, which is often a single administrator. Choosing a policy here lets you send
                these to the people who can actually reassign a system.
              </div>
            )}
            {policies.length === 0 && !policiesLoading && (
              <div className="mt-2 text-[12.5px] text-v2-ink-muted">
                No escalation policies have been set up for this customer yet. You can create one on
                the Escalation Policies page, or import your existing one from your identity system
                or a spreadsheet, and then return here.
              </div>
            )}
          </Card>

          <Card>
            <div className="font-semibold text-[14px] mb-1">How often to check</div>
            <div className="text-[12.5px] text-v2-ink-muted mb-3">
              Most people won't need to change these. Shorter times mean a departure is noticed
              sooner; longer times mean less background work.
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <NumberField
                label="Check for departures every"
                unit="minutes"
                hint="Between 5 minutes and 24 hours. This is the usual check."
                value={cfg.leaverIntervalMinutes}
                disabled={saving}
                onCommit={(v) => patch({ leaverIntervalMinutes: v }, 'The departure check interval')}
              />
              <NumberField
                label="Deeper check every"
                unit="minutes"
                hint="Between 1 hour and 7 days. Catches owners whose account was deleted outright. It looks at every system, so it runs less often."
                value={cfg.orphanScanIntervalMinutes}
                disabled={saving}
                onCommit={(v) => patch({ orphanScanIntervalMinutes: v }, 'The deeper check interval')}
              />
              <NumberField
                label="Systems reviewed per check"
                unit="systems"
                hint="Between 1 and 5000. Anything not covered in one round is picked up on the next."
                value={cfg.leaverBatchSize}
                disabled={saving}
                onCommit={(v) => patch({ leaverBatchSize: v }, 'The number of systems per check')}
              />
            </div>
            <div className="mt-4 flex items-start gap-2">
              <input
                id="susp"
                type="checkbox"
                checked={!!cfg.suspensionOrphans}
                disabled={saving}
                onChange={(e) => patch({ suspensionOrphans: e.target.checked }, 'The suspended-customer setting')}
              />
              <label htmlFor="susp" className="text-[13px]">
                Also act when an owner is inactive only because their customer is suspended
                <div className="text-[12.5px] text-v2-ink-muted">
                  Off is recommended. A suspended customer is usually restored later, and their
                  people become active again — acting now would throw away sign-offs that are about
                  to be valid. These cases are listed either way, so you can still see them.
                </div>
              </label>
            </div>
          </Card>

          <div className="text-[12px] text-v2-ink-muted">
            {cfg.updatedAt
              ? `Last changed ${new Date(cfg.updatedAt).toLocaleString()}.`
              : 'These settings have never been changed for this customer.'}
            {' '}Changes take effect within a few minutes. No restart is needed.
          </div>
        </>
      )}

      {!cfg && !loading && !error && (
        <Card padding="none">
          <EmptyState
            icon={<ShieldAlert size={22} />}
            title="Choose a customer to begin"
            description="Pick a customer above to see how the platform handles their systems when someone leaves the company."
          />
        </Card>
      )}
    </div>
  );
}

function modeLabel(mode) {
  const m = MODES.find((x) => x.id === String(mode || '').toUpperCase());
  return m ? m.label : String(mode || 'Unknown');
}

/** Turn a transport error into something the reader can act on. */
function friendly(e) {
  if (e && e.status === 403) return 'You do not have permission to change this customer.';
  if (e && e.status === 400) return e.message || 'Please check the value and try again.';
  return 'Check your connection and try again.';
}

/**
 * Saves when you leave the field or press Enter — one save per change you meant to make,
 * rather than one per digit typed.
 */
function NumberField({ label, unit, hint, value, disabled, onCommit }) {
  const [draft, setDraft] = useState(String(value ?? ''));
  useEffect(() => { setDraft(String(value ?? '')); }, [value]);
  const commit = () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || String(value) === draft) { setDraft(String(value ?? '')); return; }
    onCommit(n);
  };
  return (
    <div>
      <Label>{label} <span className="text-v2-ink-muted font-normal">({unit})</span></Label>
      <Input
        type="number"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
      />
      <div className="text-[12px] text-v2-ink-muted mt-1">{hint}</div>
    </div>
  );
}
